import { pickWidgetLocale } from "@studiolite/booking";

/**
 * Langue demandee par le snippet, telle qu'on la renvoie a l'API.
 *
 * Le widget traduit ses propres libelles cote client, mais le contenu du studio
 * (titres de tarifs, descriptions de cours) est choisi par le serveur : lui
 * seul sait quelles traductions existent et vers quoi retomber quand la langue
 * demandee manque.
 *
 * On lit `?locale=` directement plutot que la langue d'i18next : elle vient du
 * meme endroit que `studioId`, elle est disponible avant que i18next soit
 * initialise, et deux widgets de la meme page peuvent demander deux langues.
 */
export function getWidgetLocale(): string | null {
  if (resolvedLocale) return resolvedLocale;
  if (typeof window === "undefined") return null;

  const requested = new URLSearchParams(window.location.search).get("locale");
  return requested && requested !== "auto" ? requested : null;
}

let resolvedLocale: string | null = null;

/**
 * La langue du widget, fixee au chargement du studio (studio-store), avant
 * le premier appel d'API (Run 4) : data-locale du snippet, sinon la langue
 * fixee dans l'admin (FR / DE / EN), sinon le <html lang> de la page hote
 * (envoye par embed.js en `pageLocale`), sinon la langue du studio.
 * « Automatique » (null, ou l'ancien "auto") suit la page.
 */
export function resolveWidgetLocale(
  widgetLocale: string | null | undefined,
  studioLocale: string | null | undefined,
): string {
  const params =
    typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  resolvedLocale = pickWidgetLocale({
    snippet: params.get("locale"),
    admin: widgetLocale,
    page: params.get("pageLocale"),
    studio: studioLocale,
  });
  return resolvedLocale;
}

/**
 * Ajoute `locale` a une URL d'API deja construite, en respectant le `?` ou `&`
 * qui s'y trouve.
 *
 * Le parametre fait partie de la cle SWR des appelants : deux widgets voisins
 * en francais et en anglais ne doivent pas se partager une reponse.
 */
export function withLocale(url: string): string {
  const locale = getWidgetLocale();
  if (!locale) return url;

  const separator = url.includes("?") ? "&" : "?";

  return `${url}${separator}locale=${encodeURIComponent(locale)}`;
}
